import { useCallback, useEffect, useMemo, useRef } from "react";
import { shallow } from "zustand/shallow";

import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import * as areenaHistory from "../../service/areena-history";
import { usePlayerStore } from "../../store/usePlayerStore";

export const useHistory = ({ hasError }: { hasError: boolean }): void => {
  const { getState, subscribe, useStoreState } = usePlayerStore();
  const isLive = useStoreState.isLive();
  const { loggedIn } = useStoreState.loginData();
  const programId = useStoreState.programId();
  const { playbackHistory } = useEmbedParametersContext();

  const currentTimeByIdRef = useRef<{ [id: string]: number }>({});
  const lastSavedRef = useRef<{ programId: string; time: number } | null>(null);

  const historyEnabled = useMemo(
    () => programId && playbackHistory && loggedIn && !hasError && !isLive,
    [hasError, programId, isLive, loggedIn, playbackHistory]
  );

  const saveHistory = useCallback((id: string, time: number) => {
    if (!time) {
      return;
    }
    const lastSaved = lastSavedRef.current;
    if (lastSaved && lastSaved.programId === id && lastSaved.time === time) {
      return;
    }
    lastSavedRef.current = { programId: id, time };
    areenaHistory.save({ programId: id, time });
  }, []);

  const subscribeCurrentTime = useCallback(() => {
    return subscribe(
      state => state.currentTime,
      currentTime => {
        // Save the current time for the current id, so we can get the last currentTime of the id
        // after media changed
        const id = getState().programId;
        if (id) {
          currentTimeByIdRef.current[id] = currentTime;
        }
      }
    );
  }, [getState, subscribe]);

  const subscribeMediaChanged = useCallback(() => {
    // Save the history of the previous media after media changed
    return subscribe(
      state => state.programId,
      (_, previousId) => {
        if (previousId) {
          const progressInSeconds = currentTimeByIdRef.current[previousId];
          if (progressInSeconds !== undefined) {
            saveHistory(previousId, progressInSeconds);
          }
        }
      }
    );
  }, [saveHistory, subscribe]);

  const subscribePlayerStateChanged = useCallback(() => {
    return subscribe(
      state => ({
        isDestroyed: state.isDestroyed,
        isPaused: state.isPaused,
        isSeeking: state.isSeeking
      }),
      (values, prevValues) => {
        const onDestroyed =
          values.isDestroyed && !prevValues.isDestroyed && !values.isPaused;
        const onPaused = values.isPaused && !prevValues.isPaused;
        const onAfterSeek = !values.isSeeking && prevValues.isSeeking;

        if (programId && (onDestroyed || onPaused || onAfterSeek)) {
          const progressInSeconds = currentTimeByIdRef.current[programId];
          if (progressInSeconds !== undefined) {
            saveHistory(programId, progressInSeconds);
          }
        }
      },
      {
        equalityFn: shallow
      }
    );
  }, [programId, saveHistory, subscribe]);

  useEffect(() => {
    if (!historyEnabled || !programId) return;

    const unsubCurrentTime = subscribeCurrentTime();
    const unsubMediaChanged = subscribeMediaChanged();
    const unsubPlayerStateChanged = subscribePlayerStateChanged();

    const visibilityChange = () => {
      if (document.visibilityState === "hidden") {
        saveHistory(programId, currentTimeByIdRef.current[programId] ?? 0);
      }
    };

    document.addEventListener("visibilitychange", visibilityChange);

    return () => {
      unsubCurrentTime();
      unsubMediaChanged();
      unsubPlayerStateChanged();
      document.removeEventListener("visibilitychange", visibilityChange);
    };
  }, [
    historyEnabled,
    programId,
    useStoreState,
    saveHistory,
    subscribeCurrentTime,
    subscribeMediaChanged,
    subscribePlayerStateChanged
  ]);
};
